/**
 * EditForm Utility Functions
 *
 * Pure functions that handle the implementation details.
 * Extracted to keep EditForm.tsx readable and focused on the flow.
 */

import type { BookingCard } from "@/hooks/booking/useBookings";
import type {
  IBooking,
  IPostBooking,
  IServiceCustomer,
  IServiceEmployee,
  IServiceProject,
} from "@/services/types";
import { BookingType } from "@/services/types";
import type { EditFormState, EditFormStep, SourceSelectionItem } from "./types";
import { isPublicHoliday } from "@/utils/date/holidays";

// ============================================================
// BOOKING COLLECTION
// ============================================================

/** Target entity union type */
type TargetEntity =
  | IBooking
  | IServiceCustomer
  | IServiceEmployee
  | null
  | undefined;

/**
 * Collects all bookings from selected cards or single booking target
 */
export function collectBookings(
  targetEntity: TargetEntity,
  selectedCards: BookingCard[],
  isBookingTarget: (t: TargetEntity) => t is IBooking,
): IBooking[] {
  if (isBookingTarget(targetEntity)) {
    return [targetEntity];
  }
  return selectedCards.flatMap((card) => card.bookings);
}

/**
 * Groups bookings by project with aggregated stats.
 * Absence bookings (b_type === ABSENCE, project: null) are grouped
 * under a virtual "Fravær" entry with id: -1.
 */
export function groupBookingsByProject(
  bookings: IBooking[],
): SourceSelectionItem[] {
  const projectMap = new Map<number, SourceSelectionItem>();

  for (const booking of bookings) {
    // Absence bookings without a project → virtual "Fravær" group
    if (booking.b_type === BookingType.ABSENCE && !booking.project) {
      const existing = projectMap.get(-1);
      if (existing) {
        existing.bookingCount++;
        existing.totalHours += booking.hours;
        existing.avgHours = existing.totalHours / existing.bookingCount;
      } else {
        projectMap.set(-1, {
          id: -1,
          name: "Fravær",
          bookingCount: 1,
          totalHours: booking.hours,
          avgHours: booking.hours,
          isAbsence: true,
        });
      }
      continue;
    }

    if (!booking.project) continue;

    const existing = projectMap.get(booking.project.id);
    if (existing) {
      existing.bookingCount++;
      existing.totalHours += booking.hours;
      existing.avgHours = existing.totalHours / existing.bookingCount;
    } else {
      projectMap.set(booking.project.id, {
        id: booking.project.id,
        name: booking.project.name,
        code: booking.project.code,
        bookingCount: 1,
        totalHours: booking.hours,
        avgHours: booking.hours,
        isAbsence: booking.b_type === BookingType.ABSENCE,
      });
    }
  }

  return Array.from(projectMap.values());
}

/**
 * Groups bookings by customer with aggregated stats
 */
export function groupBookingsByCustomer(
  bookings: IBooking[],
): SourceSelectionItem[] {
  const customerMap = new Map<number, SourceSelectionItem>();

  for (const booking of bookings) {
    if (!booking.customer) continue;

    const existing = customerMap.get(booking.customer.id);
    if (existing) {
      existing.bookingCount++;
      existing.totalHours += booking.hours;
      existing.avgHours = existing.totalHours / existing.bookingCount;
    } else {
      customerMap.set(booking.customer.id, {
        id: booking.customer.id,
        name: booking.customer.name,
        bookingCount: 1,
        totalHours: booking.hours,
        avgHours: booking.hours,
      });
    }
  }

  return Array.from(customerMap.values());
}

// ============================================================
// BOOKING TYPE DETECTION
// ============================================================

/**
 * Determines the effective booking type from actual bookings
 */
export function determineBookingType(
  bookings: IBooking[],
  fallback: "hard" | "soft",
): "hard" | "soft" {
  const hasSoftBookings = bookings.some(
    (b) => b.b_type === BookingType.SOFT_BOOKING,
  );
  if (hasSoftBookings) return "soft";

  const hasHardBookings = bookings.some(
    (b) => b.b_type === BookingType.HARD_BOOKING,
  );
  if (hasHardBookings) return "hard";

  return fallback;
}

// ============================================================
// INITIAL STATE
// ============================================================

/**
 * Determines initial step based on selection complexity
 */
export function determineInitialStep(
  bookingType: "hard" | "soft",
  projectsInSelection: SourceSelectionItem[],
  customersInSelection: SourceSelectionItem[],
): EditFormStep {
  if (bookingType === "hard" && projectsInSelection.length > 1)
    return "selectSource";
  if (bookingType === "soft" && customersInSelection.length > 1)
    return "selectSource";
  return "edit";
}

/** Pseudo-project constant for absence bookings */
export const ABSENCE_PROJECT = {
  id: -1,
  code: "",
  name: "Absence",
  is_archived: false,
};

/**
 * Finds initial source project from selection
 * For absence bookings with pseudo "Fravær" project (id: -1), returns ABSENCE_PROJECT constant
 */
export function findInitialSourceProject(
  projectsInSelection: SourceSelectionItem[],
  activeProjects: IServiceProject[],
): IServiceProject | null {
  if (projectsInSelection.length !== 1) return null;

  const selectionItem = projectsInSelection[0];

  // For absence bookings with pseudo "Fravær" project (id <= 0 or isAbsence flag),
  // return ABSENCE_PROJECT constant so it can be identified later
  if (selectionItem.isAbsence || selectionItem.id < 0) {
    return ABSENCE_PROJECT;
  }

  return activeProjects.find((p) => p.id === selectionItem.id) ?? null;
}

/**
 * Finds initial source customer from selection or target
 */
export function findInitialSourceCustomer(
  customersInSelection: SourceSelectionItem[],
  activeCustomers: IServiceCustomer[],
  targetEntity: TargetEntity,
  bookings: IBooking[],
  isCustomerTarget: (t: TargetEntity) => t is IServiceCustomer,
): IServiceCustomer | null {
  // Single customer in selection
  if (customersInSelection.length === 1) {
    const item = customersInSelection[0];
    const found = activeCustomers.find((c) => c.id === item.id);
    if (found) return found;
    return { id: item.id, name: item.name } as IServiceCustomer;
  }

  // Target is a customer
  if (isCustomerTarget(targetEntity)) {
    return targetEntity;
  }

  // First booking with customer
  const firstWithCustomer = bookings.find((b) => b.customer);
  if (firstWithCustomer?.customer) {
    const found = activeCustomers.find(
      (c) => c.id === firstWithCustomer.customer!.id,
    );
    return found ?? firstWithCustomer.customer;
  }

  return null;
}

// ============================================================
// VALIDATION
// ============================================================

/**
 * Checks if the current selection has a valid target for submission
 */
export function hasValidTarget(
  state: EditFormState,
  bookingType: "hard" | "soft",
  targetEntity: TargetEntity,
  isCustomerTarget: (t: TargetEntity) => t is IServiceCustomer,
): boolean {
  if (state.isAbsence) return true;

  if (bookingType === "soft") {
    return isCustomerTarget(targetEntity) || state.targetCustomer !== null;
  }

  if (bookingType === "hard") {
    return (
      state.targetProject !== null && state.targetProject.name !== "Absence"
    );
  }

  return false;
}

/**
 * Checks if any day would exceed max hours after edit
 */
export function checkOverbookedDays(
  bookingType: "hard" | "soft",
  sourceBookings: IBooking[],
  selectedCards: BookingCard[],
  newHours: number,
  maxHoursPerDay: number,
): boolean {
  if (bookingType !== "hard") return false;

  const editedIds = new Set(sourceBookings.map((b) => b.id));

  return selectedCards.some((card) => {
    const unchangedBookings = card.bookings.filter((b) => !editedIds.has(b.id));
    const unchangedHours = unchangedBookings.reduce(
      (sum, b) => sum + b.hours,
      0,
    );
    const editedCount = card.bookings.length - unchangedBookings.length;
    return unchangedHours + newHours * editedCount > maxHoursPerDay;
  });
}

// ============================================================
// POST BOOKING BUILDER
// ============================================================

/**
 * Builds IPostBooking array from source bookings with updated values
 */
export function buildPostBookings(
  sourceBookings: IBooking[],
  state: EditFormState,
  bookingType: "hard" | "soft",
): IPostBooking[] {
  return sourceBookings.map((booking) => {
    let bType = booking.b_type;
    let projectId = booking.project?.id ?? null;
    let customerId = booking.customer?.id ?? null;

    if (state.isAbsence) {
      bType = BookingType.ABSENCE;
      // When converting to absence, preserve the original booking's project association
      // Use sourceProject if available (selected in multi-project mode), otherwise use booking's project
      // This links the absence to the project the employee was absent from
      projectId = state.sourceProject?.id ?? booking.project?.id ?? null;
    } else if (bookingType === "hard" && state.targetProject) {
      bType = BookingType.HARD_BOOKING;
      projectId = state.targetProject.id;
    } else if (bookingType === "soft" && state.targetCustomer) {
      bType = BookingType.SOFT_BOOKING;
      customerId = state.targetCustomer.id;
    }

    return {
      id: booking.id,
      date: booking.date,
      project: projectId,
      employee: booking.employee?.id ?? null,
      activity: booking.activity?.id ?? 1,
      b_type: bType,
      hours: state.hours,
      customer: customerId,
    };
  });
}

// ============================================================
// DISPLAY HELPERS
// ============================================================

/**
 * Formats the source label for display
 */
export function formatSourceLabel(
  bookingType: "hard" | "soft",
  sourceProject: IServiceProject | null,
  sourceCustomer: IServiceCustomer | null,
  bookings: IBooking[],
): string {
  if (bookingType === "hard") {
    if (!sourceProject) return "ukjent";
    if (sourceProject.name === "Absence") return sourceProject.name;
    return `${sourceProject.code} - ${sourceProject.name}`;
  }

  if (sourceCustomer?.name) return sourceCustomer.name;

  const firstWithCustomer = bookings.find((b) => b.customer?.name);
  if (firstWithCustomer?.customer?.name) return firstWithCustomer.customer.name;

  return "ukjent";
}

/**
 * Calculates median of numbers, rounded to 0.5
 */
export function calculateMedianHours(hours: number[]): number {
  if (hours.length === 0) return 8;
  const sorted = [...hours].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const median =
    sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  return Math.round(median * 2) / 2;
}

/**
 * Gets initial hours from source bookings.
 * When sourceId === -1, filters for absence bookings.
 */
export function getInitialHoursForSource(
  sourceId: number | null,
  bookings: IBooking[],
  bookingType: "hard" | "soft",
  defaultHours: number,
): number {
  if (sourceId == null) return defaultHours;

  const matchesSource = (b: IBooking) => {
    if (sourceId === -1) return b.b_type === BookingType.ABSENCE;
    if (bookingType === "hard") return b.project?.id === sourceId;
    return b.customer?.id === sourceId;
  };
  const sourceBookings = bookings.filter(matchesSource);

  if (sourceBookings.length === 0) return defaultHours;
  return calculateMedianHours(sourceBookings.map((b) => b.hours));
}

// ============================================================
// WEEKEND/HOLIDAY HELPERS
// ============================================================

/**
 * Counts weekend bookings (Saturday and Sunday).
 */
export function countWeekendBookings(bookings: IBooking[]): number {
  return bookings.filter((booking) => {
    const day = booking.date.getDay();
    return day === 0 || day === 6;
  }).length;
}

/**
 * Counts holiday bookings (official public holidays only).
 */
export function countHolidayBookings(bookings: IBooking[]): number {
  return bookings.filter((booking) => isPublicHoliday(booking.date)).length;
}

/**
 * Counts unique bookings that should trigger a warning.
 * A booking on a day that is both weekend and holiday is counted only once.
 */
export function countWarningRedBookings(
  bookings: IBooking[],
  options: { skipWeekends: boolean; skipHolidays: boolean },
): number {
  const { skipWeekends, skipHolidays } = options;

  return bookings.filter((booking) => {
    const day = booking.date.getDay();
    const isWeekend = day === 0 || day === 6;
    const isHoliday = isPublicHoliday(booking.date);

    return (skipWeekends && isWeekend) || (skipHolidays && isHoliday);
  }).length;
}

/**
 * Filters out weekend bookings and/or holiday bookings depending on options.
 */
export function filterRedBookings(
  bookings: IBooking[],
  options: { skipWeekends: boolean; skipHolidays: boolean },
): IBooking[] {
  const { skipWeekends, skipHolidays } = options;

  return bookings.filter((booking) => {
    const day = booking.date.getDay();
    const isWeekend = day === 0 || day === 6;
    const isHoliday = isPublicHoliday(booking.date);

    if (skipWeekends && isWeekend) return false;
    if (skipHolidays && isHoliday) return false;

    return true;
  });
}
